import { supabase } from "@/api/supabaseClient";
import { allowNextSignedIn, cancelExplicitSignIn } from "@/auth/webSignOutLatch";
import LogoFerinaCura from "@/assets/logos/Logo_FerinaCura.svg";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { routes } from "@/constants/routes";
import { getShadowStyle } from "@/theme/shadowStyle";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { Card } from "@/ui/components/Card";
import { ErrorText } from "@/ui/components/ErrorText";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { LinkText } from "@/ui/components/LinkText";
import { TextButton } from "@/ui/components/TextButton";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import {
  HeartbeatIcon,
  KeyIcon,
  NotebookIcon,
  SignInIcon,
  UserPlusIcon,
  UsersIcon,
} from "phosphor-react-native";
import { useEffect, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

/** Kleinere Hero-Bilder (z. B. max. 1200px breit) laden schneller und bleiben besser mit dem Text-Slide im Takt. */
const HERO_IMAGES = [
  require("@/assets/images/hero_slide_1.jpg"),
  require("@/assets/images/hero_slide_2.jpg"),
  require("@/assets/images/hero_slide_3.jpg"),
];

const BRAND_SUBTITLE = "App für die Wildtierhilfe";

const HERO_SLIDES = [
  {
    kicker: "Wildtierhilfe • Pflege • Dokumentation",
    kickerIcon: HeartbeatIcon,
    kickerRight: "Fürs Ehrenamt gemacht",
    title: "Schnell helfen – sauber dokumentieren",
    text: "FerinaCura unterstützt euch bei Fundtieraufnahme, Pflege-Workflow und Nachverfolgung – mobil im Einsatz und am Rechner in der Pflegestelle.",
  },
  {
    kicker: "Übersicht • Verlauf • Fotos",
    kickerIcon: NotebookIcon,
    kickerRight: "klar strukturiert",
    title: "Alles an einem Ort",
    text: "Maßnahmen, Gewichte, Notizen und Fotos: schnell erfasst, zuverlässig wieder auffindbar – für eine saubere Dokumentation.",
  },
  {
    kicker: "Pflegestellen • Stationen • Teams",
    kickerIcon: UsersIcon,
    kickerRight: "gemeinsam",
    title: "Gemeinsam – auch im Ehrenamt",
    text: "Weniger Zettelwirtschaft, klarere Abläufe – mehr Zeit fürs Tier.",
  },
];

const AUTO_SLIDE_MS = 7000;

/** Lädt das nächste Hero-Bild; nach onLoad wird gewechselt. Fallback nach 500 ms, falls onLoad nicht feuert (z. B. gecacht). */
function HeroPreloadImage({
  source,
  onLoaded,
}: {
  source: (typeof HERO_IMAGES)[number];
  onLoaded: () => void;
}) {
  const onLoadedRef = useRef(onLoaded);
  const doneRef = useRef(false);
  onLoadedRef.current = onLoaded;

  const handleLoaded = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onLoadedRef.current();
  };

  useEffect(() => {
    doneRef.current = false;
    const t = setTimeout(handleLoaded, 500);
    return () => clearTimeout(t);
  }, [source]);

  return (
    <View
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
      }}
    >
      <Image
        source={source}
        style={{ width: "100%", height: "100%", opacity: 0 }}
        contentFit="cover"
        onLoad={handleLoaded}
      />
    </View>
  );
}

export default function LoginScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  // Auf Native: Breite beim ersten Render fixieren, damit Tastatur-Öffnen (Dimensions-Änderung)
  // kein Layout-Flackern und Fokus-Verlust in E-Mail/Passwort auslöst.
  const widthStableRef = useRef<number | null>(null);
  if (Platform.OS !== "web" && widthStableRef.current == null) {
    widthStableRef.current = width;
  }
  const widthForLayout = Platform.OS === "web" ? width : (widthStableRef.current ?? width);
  const wide = widthForLayout >= WIDE_VIEWPORT_BREAKPOINT;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [heroSlide, setHeroSlide] = useState(0);
  const [nextSlide, setNextSlide] = useState<number | null>(null);
  const autoSlideRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heroSlideRef = useRef(0);
  const nextSlideRef = useRef<number | null>(null);
  const formContainerRef = useRef<View>(null);
  heroSlideRef.current = heroSlide;
  nextSlideRef.current = nextSlide;

  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy;

  // Web: Form-Container nur mit data-Attribut markieren (für Fokus-Rahmen ausblenden). Kein programmatischer Fokus,
  // damit Klick ins E-Mail-Feld nicht „rauswirft“ (Fokus-Konflikt mit View + Input).
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const el = formContainerRef.current as unknown as HTMLElement | null;
    if (el) el.setAttribute("data-login-form", "");
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const id = "ferina-login-form-focus-hide";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      [data-login-form]:focus {
        outline: none !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  // Hero: Wechsel erst wenn neues Bild geladen ist, damit Text und Bild synchron wechseln
  const goToSlide = (index: number) => {
    if (index === heroSlide) return;
    setNextSlide(index);
  };

  useEffect(() => {
    autoSlideRef.current = setInterval(() => {
      if (nextSlideRef.current !== null) return;
      setNextSlide((heroSlideRef.current + 1) % HERO_SLIDES.length);
    }, AUTO_SLIDE_MS);
    return () => {
      if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    };
  }, []);

  const doLogin = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    allowNextSignedIn();
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      router.replace("/home");
    } catch (e: unknown) {
      cancelExplicitSignIn();
      setError((e as { message?: string })?.message ?? "Login fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const pageBgStyle =
    Platform.OS === "web" && theme.pageBgWebGradient
      ? { background: theme.pageBgWebGradient }
      : { backgroundColor: theme.color.bg };

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={[
        {
          flexGrow: 1,
          minHeight: "100%",
          justifyContent: "center",
          alignItems: "center",
          padding: wide ? 28 : 18,
        },
        pageBgStyle,
      ]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="none"
    >
      <View
        style={{
          width: "100%",
          maxWidth: 1040,
          flexDirection: wide ? "row" : "column",
          gap: wide ? 18 : 16,
          alignItems: wide ? "stretch" : "center",
        }}
      >
        {/* Hero-Panel: nur auf breiten Formaten (≥900px) */}
        {wide && (
          <View
            style={{
              flex: 1.2,
              width: undefined,
              maxWidth: 1040,
              minHeight: 400,
              borderWidth: 1,
              borderColor: theme.color.border,
              borderRadius: theme.radius.panel,
              overflow: "hidden",
              ...getShadowStyle(theme.panelShadow),
            }}
          >
            <View style={{ flex: 1, flexDirection: "column" }}>
              {/* Bild: füllt den verfügbaren Platz oberhalb des Textblocks */}
              <View
                style={{
                  flex: 1,
                  minHeight: 220,
                  position: "relative",
                }}
              >
                <Image
                  source={HERO_IMAGES[heroSlide]}
                  style={{ width: "100%", height: "100%" }}
                  contentFit="cover"
                />
                {/* Nächstes Bild vorladen; erst nach onLoad wechseln wir heroSlide, damit Text und Bild synchron sind */}
                {nextSlide !== null && (
                  <HeroPreloadImage
                    source={HERO_IMAGES[nextSlide]}
                    onLoaded={() => {
                      setHeroSlide(nextSlide);
                      setNextSlide(null);
                    }}
                  />
                )}
                {/* Pagination-Dots */}
                <View
                  style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 16,
                    transform: "translateX(-50%)",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    paddingVertical: 8,
                    paddingHorizontal: 10,
                    borderRadius: 999,
                    backgroundColor: theme.color.heroPaginationBg,
                    borderWidth: 1,
                    borderColor: theme.color.border,
                    alignSelf: "center",
                  }}
                >
                  {HERO_SLIDES.map((_, i) => (
                    <Pressable
                      key={i}
                      onPress={() => {
                        goToSlide(i);
                        if (autoSlideRef.current) {
                          clearInterval(autoSlideRef.current);
                          autoSlideRef.current = setInterval(() => {
                            if (nextSlideRef.current !== null) return;
                            setNextSlide(
                              (heroSlideRef.current + 1) % HERO_SLIDES.length,
                            );
                          }, AUTO_SLIDE_MS);
                        }
                      }}
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 999,
                        backgroundColor:
                          i === heroSlide
                            ? theme.color.primary
                            : theme.color.muted,
                      }}
                      accessibilityLabel={`Folie ${i + 1} von ${HERO_SLIDES.length}`}
                    />
                  ))}
                </View>
              </View>
            </View>
            {/* Textbereich: Kicker-Zeile mit Abstand zum Titel, damit nichts überlappt; Höhe reduziert */}
            <View
              style={{
                flexShrink: 0,
                height: 240,
                backgroundColor: theme.color.heroPanelBg,
                borderTopWidth: 1,
                borderTopColor: theme.color.border,
              }}
            >
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{
                  paddingHorizontal: 20,
                  paddingTop: 12,
                  paddingBottom: 20,
                  flexGrow: 1,
                }}
                showsVerticalScrollIndicator={wide}
                keyboardShouldPersistTaps="handled"
              >
                {(() => {
                  const slide = HERO_SLIDES[heroSlide];
                  const KickerIcon = slide.kickerIcon;
                  return (
                    <>
                      {/* Kicker-Zeile: eigene Höhe + Abstand nach unten, überlappt den Titel nicht */}
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                          minHeight: 36,
                          marginBottom: 14,
                        }}
                      >
                        <View
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            backgroundColor: theme.color.primary,
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 13,
                            color: theme.color.muted,
                            letterSpacing: 0.2,
                            flex: 1,
                            minWidth: 0,
                          }}
                          numberOfLines={2}
                        >
                          {slide.kicker}
                        </Text>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 6,
                            flexShrink: 0,
                          }}
                        >
                          <KickerIcon size={16} color={theme.color.primary} />
                          <Text
                            style={{
                              fontWeight: "700",
                              color: theme.color.primary,
                              fontSize: 13,
                            }}
                          >
                            {slide.kickerRight}
                          </Text>
                        </View>
                      </View>
                      <Text
                        style={{
                          fontSize: wide ? 24 : 18,
                          fontWeight: "800",
                          color: theme.color.text,
                          letterSpacing: 0.2,
                          marginBottom: 8,
                          marginTop: 0,
                        }}
                      >
                        {slide.title}
                      </Text>
                      <Text
                        style={{
                          fontSize: 14,
                          lineHeight: 20,
                          color: theme.color.textSecondary,
                          marginTop: 6,
                          maxWidth: 520,
                        }}
                      >
                        {slide.text}
                      </Text>
                    </>
                  );
                })()}
              </ScrollView>
            </View>
          </View>
        )}

        {/* Login-Panel: äußere Karte (Rahmen, Schatten, surface) + innen Cards mit eigenem Hintergrund */}
        <View
          style={{
            flex: wide ? 0.8 : undefined,
            width: wide ? undefined : "100%",
            maxWidth: 480,
            borderWidth: 1,
            borderColor: theme.color.border,
            borderRadius: theme.radius.panel,
            overflow: "hidden",
            backgroundColor: theme.color.surface,
            padding: wide ? 26 : 18,
            justifyContent: "center",
            gap: 16,
          }}
        >
          {/* Brand */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              marginBottom: 4,
            }}
          >
            <View
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                backgroundColor: theme.color.surface,
                borderWidth: 1,
                borderColor: theme.color.border,
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <LogoFerinaCura
                width={56}
                height={56}
                fill={theme.color.primary}
              />
            </View>
            <View>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: "800",
                  color: theme.color.text,
                  letterSpacing: 0.2,
                }}
              >
                FerinaCura
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  color: theme.color.muted,
                }}
              >
                {BRAND_SUBTITLE}
              </Text>
            </View>
          </View>

          {/* Card: Formular (Control .card) */}
          <Card
            style={{
              backgroundColor: theme.color.surfaceStrong,
              borderColor: theme.color.border,
              borderRadius: theme.radius.card,
              padding: theme.space.cardPadding,
              ...getShadowStyle(theme.cardShadow),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                marginBottom: 13,
              }}
            >
              <KeyIcon
                size={18}
                color={theme.color.primary}
              />
              <Text
                style={{
                  fontWeight: "800",
                  fontSize: 14,
                  color: theme.color.text,
                  letterSpacing: 0.2,
                  marginBottom: 4,
                }}
              >
              Anmelden
              </Text>
            </View>

            <View
              ref={formContainerRef}
              style={{ gap: 12, marginTop: 0 }}
              {...(Platform.OS === "web" ? { tabIndex: 0 } : {})}
            >
              <FloatingLabelInput
                label="E-Mail-Adresse"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                returnKeyType="next"
                disableGlobalClickSync
              />
              <FloatingLabelInput
                label="Passwort"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                returnKeyType="go"
                onSubmitEditing={doLogin}
                disableGlobalClickSync
              />

              {error ? <ErrorText>{error}</ErrorText> : null}

              <AppButton
                pill
                icon={SignInIcon}
                loading={busy}
                disabled={!canSubmit}
                onPress={doLogin}
                style={{
                  marginTop: 6,
                  ...(canSubmit
                    ? {
                        backgroundColor: theme.color.buttonBg,
                        ...theme.primaryButtonShadow,
                      }
                    : {}),
                }}
              >
                Anmelden
              </AppButton>

              <View style={{ marginBottom: 20, width: "100%" }}>
                <Text
                  style={{
                    color: theme.color.fine,
                    fontSize: theme.fontSize.fine,
                    lineHeight: theme.fontSize.fine * theme.fineLineHeight,
                    textAlign: "center",
                  }}
                >
                  Mit dem Login akzeptierst du unsere{" "}
                  <LinkText
                    onPress={() => {}}
                    color={theme.color.link}
                    style={{ fontSize: theme.fontSize.fine, textDecorationLine: 'underline' }}
                  >
                    Datenschutzerklärung
                  </LinkText>{" "}
                  und{" "}
                  <LinkText
                    onPress={() => {}}
                    color={theme.color.link}
                    style={{ fontSize: theme.fontSize.fine, textDecorationLine: 'underline' }}
                  >
                    Nutzungsbedingungen
                  </LinkText>
                  .
                </Text>
              </View>

              <TextButton
                onPress={() => router.push(routes.forgotPassword)}
                color={theme.color.accent}
              >
                Passwort vergessen?
              </TextButton>
            </View>
          </Card>

          <Card
            variant="dashed"
            style={{
              backgroundColor: theme.color.surface,
              borderColor: theme.color.border,
              borderRadius: theme.radius.card,
              padding: theme.space.registerPadding,
              paddingHorizontal: 16,
              gap: 6,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              <UserPlusIcon
                size={18}
                color={theme.color.primary}
              />
              <Text
                style={{
                  fontWeight: "800",
                  fontSize: 14,
                  color: theme.color.text,
                  letterSpacing: 0.2,
                }}
              >
                Neu hier?
              </Text>
            </View>
            <Text
              style={{
                fontSize: 12.5,
                color: theme.color.textSecondary,
              }}
            >
              Zugang gibt es nur mit Einladung. Ein Superadmin, ein Admin oder der Vorstand schickt den Link.
            </Text>
          </Card>

          <Text
            style={{
              color: theme.color.footer,
              fontSize: theme.fontSize.footer,
              textAlign: "center",
            }}
          >
            © 2026 Suspicious Companions
          </Text>
        </View>
      </View>
    </ScrollView>
  );
}
